import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDeveloperContext } from '@/hooks/useDeveloperContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, Users, CalendarCheck, DollarSign } from 'lucide-react';

const STAGE_ORDER = ['new', 'contacted', 'qualified', 'site_visit', 'negotiation', 'won', 'lost'];
const STAGE_COLORS: Record<string, string> = {
  new: '#3b82f6', contacted: '#8b5cf6', qualified: '#06b6d4',
  site_visit: '#f59e0b', negotiation: '#ec4899', won: '#10b981', lost: '#ef4444',
};

export default function DeveloperOverview() {
  const { ctx, isLoading: ctxLoading } = useDeveloperContext();
  const developerId = ctx.developerId;

  const { data: developerUserId } = useQuery({
    queryKey: ['dev-user-id', developerId],
    enabled: !!developerId,
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_profiles')
        .select('user_id')
        .eq('id', developerId!)
        .maybeSingle();
      return data?.user_id as string | undefined;
    },
  });

  // Project IDs owned by this developer (used to filter leads)
  const { data: projectIds = [] } = useQuery({
    queryKey: ['dev-project-ids', developerId],
    enabled: !!developerId,
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('id')
        .eq('developer_id', developerId!);
      return (data || []).map((p: any) => p.id as string);
    },
  });

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['dev-overview-leads', developerId, projectIds.length],
    enabled: !!developerId && projectIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('leads')
        .select('id, status, assigned_to, created_at')
        .in('project_id', projectIds);
      return data || [];
    },
  });

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['dev-overview-bookings', developerUserId],
    enabled: !!developerUserId,
    queryFn: async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id, booking_status, booking_amount, created_at, cancelled_at')
        .eq('developer_user_id', developerUserId!);
      return data || [];
    },
  });

  const { data: members = [] } = useQuery({
    queryKey: ['dev-overview-members', developerId],
    enabled: !!developerId,
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_team_members')
        .select('user_id, full_name, member_role, is_active')
        .eq('developer_id', developerId!)
        .eq('is_active', true);
      return data || [];
    },
  });

  if (ctxLoading) return <Skeleton className="h-96 w-full" />;
  if (!developerId) return <div className="p-6">No developer workspace found.</div>;

  // Funnel
  const funnel = STAGE_ORDER.map(stage => ({
    stage: stage.replace('_', ' '),
    key: stage,
    count: leads.filter((l: any) => l.status === stage).length,
  }));
  const totalLeads = leads.length;
  const wonLeads = funnel.find(f => f.key === 'won')?.count || 0;
  const conversionRate = totalLeads ? ((wonLeads / totalLeads) * 100).toFixed(1) : '0';

  // Booking stats
  const activeBookings = bookings.filter((b: any) => b.booking_status === 'booked' || b.booking_status === 'confirmed').length;
  const cancelledBookings = bookings.filter((b: any) => !!b.cancelled_at || b.booking_status === 'cancelled').length;
  const totalRevenue = bookings
    .filter((b: any) => !b.cancelled_at)
    .reduce((sum: number, b: any) => sum + Number(b.booking_amount || 0), 0);

  // Last 12 weeks bookings trend
  const now = new Date();
  const trend = Array.from({ length: 12 }, (_, i) => {
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - (11 - i) * 7);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);
    const count = bookings.filter((b: any) => {
      const d = new Date(b.created_at);
      return d >= weekStart && d < weekEnd;
    }).length;
    return { week: `W${i + 1}`, bookings: count };
  });

  // Member performance
  const memberPerf = members.map((m: any) => {
    const memberLeads = leads.filter((l: any) => l.assigned_to === m.user_id);
    const won = memberLeads.filter((l: any) => l.status === 'won').length;
    return {
      name: m.full_name || 'Member',
      role: m.member_role,
      assigned: memberLeads.length,
      won,
      conversion: memberLeads.length ? Math.round((won / memberLeads.length) * 100) : 0,
    };
  }).sort((a, b) => b.assigned - a.assigned);

  return (
    <div className="space-y-6 p-2">
      <div>
        <h1 className="text-2xl font-bold">Developer Overview</h1>
        <p className="text-sm text-muted-foreground">Pipeline, bookings, and team performance for {ctx.developerName}.</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Total Leads" value={totalLeads} hint={`${conversionRate}% conversion`} />
        <KpiCard icon={CalendarCheck} label="Active Bookings" value={activeBookings} hint={`${cancelledBookings} cancelled`} />
        <KpiCard icon={DollarSign} label="Booking Revenue" value={`ETB ${totalRevenue.toLocaleString()}`} />
        <KpiCard icon={TrendingUp} label="Won Leads" value={wonLeads} hint={`of ${totalLeads}`} />
      </div>

      {/* Customer KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={Users} label="New" value={funnel.find(f => f.key === 'new')?.count || 0} />
        <KpiCard icon={Users} label="Contacted" value={funnel.find(f => f.key === 'contacted')?.count || 0} />
        <KpiCard icon={TrendingUp} label="Won" value={wonLeads} />
        <KpiCard icon={Users} label="Lost" value={funnel.find(f => f.key === 'lost')?.count || 0} />
        <KpiCard icon={TrendingUp} label="Conversion" value={`${conversionRate}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Lead Funnel</CardTitle></CardHeader>
          <CardContent className="h-72">
            {leadsLoading ? <Skeleton className="h-full w-full" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="stage" type="category" width={90} />
                  <Tooltip />
                  <Bar dataKey="count">
                    {funnel.map((f, i) => <Cell key={i} fill={STAGE_COLORS[f.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Bookings — Last 12 Weeks</CardTitle></CardHeader>
          <CardContent className="h-72">
            {bookingsLoading ? <Skeleton className="h-full w-full" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Booking Status Mix</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Active', value: activeBookings, fill: '#10b981' },
                    { name: 'Cancelled', value: cancelledBookings, fill: '#ef4444' },
                    { name: 'Other', value: Math.max(0, bookings.length - activeBookings - cancelledBookings), fill: '#94a3b8' },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label
                />
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Member Performance</CardTitle></CardHeader>
          <CardContent>
            {memberPerf.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">No active team members yet.</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto">
                {memberPerf.map((m, i) => (
                  <div key={i} className="flex items-center justify-between border rounded-lg p-2.5">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{m.name}</div>
                      <Badge variant="outline" className="text-[10px] mt-0.5">{m.role.replace('developer_', '')}</Badge>
                    </div>
                    <div className="flex gap-4 text-xs text-right shrink-0">
                      <div><div className="font-bold text-base">{m.assigned}</div><div className="text-muted-foreground">Leads</div></div>
                      <div><div className="font-bold text-base text-green-600">{m.won}</div><div className="text-muted-foreground">Won</div></div>
                      <div><div className="font-bold text-base">{m.conversion}%</div><div className="text-muted-foreground">Conv.</div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint }: { icon: any; label: string; value: any; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">{label}</div>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="text-2xl font-bold mt-1">{value}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}
